import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Camera
} from "lucide-react";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";
import { SMLogo } from "@/app/components/sm-logo";
import { fetchCep } from "@/app/api/cep-client";
import { validateCPF } from "@/config/locale";
import { compressProfilePhotoForUpload } from "@/app/utils/compress-image-to-max-bytes";

export interface OnboardingData {
  // Personal Info
  fullName: string;
  preferredName: string;
  cpf: string;
  birthDate: string;
  /** Set from API after load; optional — not collected during onboarding. */
  gender?: "male" | "female" | "other" | "prefer-not-to-say";

  // Contact Info
  phone: string;
  email: string; // Will be passed from login
  
  // Address
  address: {
    cep: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  
  // Optional
  profileImage?: string;
  
  // Account info
  memberSince: string;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isCpfVerified: boolean;
  /** Set from GET /auth/me/profile when `onboarding_completed_at` is present. */
  onboardingCompletedAt?: string | null;
}

interface NewUserOnboardingProps {
  email: string; // Email already collected during signup
  fullName: string; // Full name already collected during signup
  profilePicture?: string; // Optional profile picture from Google SSO
  /** Called when user finishes. If user selected a photo, photoFile is set and parent should upload it after saving profile. */
  onComplete: (data: OnboardingData, photoFile?: File) => void | Promise<void>;
  onSkip?: () => void;
}

type OnboardingStep = "personal" | "contact" | "address" | "photo" | "review";

const BRAZILIAN_STATES = [
  { code: "AC", name: "Acre" },
  { code: "AL", name: "Alagoas" },
  { code: "AP", name: "Amapá" },
  { code: "AM", name: "Amazonas" },
  { code: "BA", name: "Bahia" },
  { code: "CE", name: "Ceará" },
  { code: "DF", name: "Distrito Federal" },
  { code: "ES", name: "Espírito Santo" },
  { code: "GO", name: "Goiás" },
  { code: "MA", name: "Maranhão" },
  { code: "MT", name: "Mato Grosso" },
  { code: "MS", name: "Mato Grosso do Sul" },
  { code: "MG", name: "Minas Gerais" },
  { code: "PA", name: "Pará" },
  { code: "PB", name: "Paraíba" },
  { code: "PR", name: "Paraná" },
  { code: "PE", name: "Pernambuco" },
  { code: "PI", name: "Piauí" },
  { code: "RJ", name: "Rio de Janeiro" },
  { code: "RN", name: "Rio Grande do Norte" },
  { code: "RS", name: "Rio Grande do Sul" },
  { code: "RO", name: "Rondônia" },
  { code: "RR", name: "Roraima" },
  { code: "SC", name: "Santa Catarina" },
  { code: "SP", name: "São Paulo" },
  { code: "SE", name: "Sergipe" },
  { code: "TO", name: "Tocantins" },
];

export function NewUserOnboarding({ email, fullName, profilePicture, onComplete, onSkip }: NewUserOnboardingProps) {
  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("personal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cepLookupLoading, setCepLookupLoading] = useState(false);
  const [cepLookupError, setCepLookupError] = useState<string | null>(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<OnboardingData>({
    fullName: fullName,
    preferredName: "",
    cpf: "",
    birthDate: "",
    phone: "",
    email: email,
    address: {
      cep: "",
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      city: "",
      state: "SP",
    },
    profileImage: profilePicture,
    memberSince: new Date().toISOString().split('T')[0],
    isEmailVerified: false,
    isPhoneVerified: false,
    isCpfVerified: false,
  });

  const updateFormData = useCallback((updates: Partial<OnboardingData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const updateAddress = useCallback((updates: Partial<OnboardingData["address"]>) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, ...updates }
    }));
  }, []);

  // Format CPF as user types
  const handleCPFChange = (value: string) => {
    const numbers = value.replace(/\D/g, "").slice(0, 11);
    let formatted = numbers;
    if (numbers.length > 3) {
      formatted = numbers.slice(0, 3) + "." + numbers.slice(3);
    }
    if (numbers.length > 6) {
      formatted = formatted.slice(0, 7) + "." + formatted.slice(7);
    }
    if (numbers.length > 9) {
      formatted = formatted.slice(0, 11) + "-" + formatted.slice(11);
    }
    updateFormData({ cpf: numbers });
  };

  // Format Phone as user types
  const handlePhoneChange = (value: string) => {
    const numbers = value.replace(/\D/g, "").slice(0, 11);
    updateFormData({ phone: numbers });
  };

  // Format CEP as user types
  const handleCEPChange = (value: string) => {
    const numbers = value.replace(/\D/g, "").slice(0, 8);
    updateAddress({ cep: numbers });
    setCepLookupError(null);
  };

  // CEP lookup via BFF (ViaCEP proxy)
  const handleCEPLookup = useCallback(async () => {
    if (formData.address.cep.length !== 8) return;
    setCepLookupError(null);
    setCepLookupLoading(true);
    try {
      const result = await fetchCep(formData.address.cep);
      updateAddress({
        street: result.street,
        neighborhood: result.neighborhood,
        city: result.city,
        state: result.state,
      });
    } catch (e) {
      setCepLookupError(e instanceof Error ? e.message : "CEP não encontrado.");
    } finally {
      setCepLookupLoading(false);
    }
  }, [formData.address.cep, updateAddress]);

  // Handle image upload: compress to ≤5MB, keep File for API upload on complete; data URL for preview
  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) {
      input.value = "";
      return;
    }
    const type = (file.type || "").toLowerCase();
    if (type && !type.startsWith("image/")) {
      toast.error("Formato inválido. Use JPG, PNG, WebP, HEIC ou SVG.");
      input.value = "";
      return;
    }
    void (async () => {
      try {
        const compressed = await compressProfilePhotoForUpload(file);
        if (!compressed.ok) {
          toast.error(compressed.error);
          input.value = "";
          return;
        }
        setProfilePhotoFile(compressed.file);
        const reader = new FileReader();
        reader.onloadend = () => {
          updateFormData({ profileImage: reader.result as string });
        };
        reader.readAsDataURL(compressed.file);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Não foi possível processar a imagem.");
      } finally {
        input.value = "";
      }
    })();
  }, [updateFormData]);

  const goToNextStep = () => {
    const steps: OnboardingStep[] = ["personal", "contact", "address", "photo", "review"];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const goToPreviousStep = () => {
    const steps: OnboardingStep[] = ["personal", "contact", "address", "photo", "review"];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const handleComplete = useCallback(async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await Promise.resolve(onComplete(formData, profilePhotoFile ?? undefined));
    } catch (e) {
      const err = e as Error & { error?: string; message?: string };
      setSubmitError(err.message ?? err.error ?? "Não foi possível salvar. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, profilePhotoFile, onComplete]);

  // Validation (CPF checksum via validateCPF; server also validates and enforces uniqueness)
  const isPersonalStepValid =
    formData.fullName.length >= 3 &&
    formData.cpf.length === 11 &&
    validateCPF(formData.cpf) &&
    formData.birthDate !== "";
  
  const isContactStepValid = formData.phone.length >= 10;
  
  const isAddressStepValid = formData.address.cep.length === 8 &&
                              formData.address.street !== "" &&
                              formData.address.number !== "" &&
                              formData.address.neighborhood !== "" &&
                              formData.address.city !== "" &&
                              formData.address.state !== "";

  const steps = [
    { id: "personal", label: "Pessoal", icon: User },
    { id: "contact", label: "Contato", icon: Phone },
    { id: "address", label: "Endereço", icon: MapPin },
    { id: "photo", label: "Foto", icon: Camera },
    { id: "review", label: "Revisão", icon: CheckCircle },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  // Format display values
  const formatCPFDisplay = (cpf: string) => {
    if (cpf.length <= 3) return cpf;
    if (cpf.length <= 6) return `${cpf.slice(0, 3)}.${cpf.slice(3)}`;
    if (cpf.length <= 9) return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6)}`;
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
  };

  const formatPhoneDisplay = (phone: string) => {
    if (phone.length <= 2) return phone;
    if (phone.length <= 6) return `(${phone.slice(0, 2)}) ${phone.slice(2)}`;
    if (phone.length <= 10) return `(${phone.slice(0, 2)}) ${phone.slice(2, 6)}-${phone.slice(6)}`;
    return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7)}`;
  };

  const formatCEPDisplay = (cep: string) => {
    if (cep.length <= 5) return cep;
    return `${cep.slice(0, 5)}-${cep.slice(5)}`;
  };

  const formatDateDisplay = (date: string) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <SMLogo className="w-16 h-16 drop-shadow-lg" />
          </div>
          <h1 className="text-3xl mb-2">Complete seu perfil</h1>
          <p className="text-muted-foreground">
            Preencha suas informações para começar a usar o akihub
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-md mx-auto">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStepIndex === index;
              const isCompleted = currentStepIndex > index;
              
              return (
                <div key={step.id} className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" :
                    isCompleted ? "bg-green-500 text-white" :
                    "bg-secondary text-muted-foreground"
                  }`}>
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs ${isActive ? "text-primary font-medium" : "text-muted-foreground"}`}>
                    {step.label}
                  </span>
                  {index < steps.length - 1 && (
                    <div className={`hidden sm:block absolute h-0.5 w-full top-5 left-1/2 -z-10 ${
                      isCompleted ? "bg-green-500" : "bg-secondary"
                    }`} style={{ maxWidth: "calc(100% / 5)" }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
          {/* Step: Personal Information */}
          {currentStep === "personal" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl mb-1 flex items-center gap-2">
                  <User className="w-6 h-6 text-primary" />
                  Informações Pessoais
                </h2>
                <p className="text-muted-foreground text-sm">
                  Olá, {formData.fullName}! Precisamos de mais algumas informações
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Nome de Preferência
                  </label>
                  <Input
                    type="text"
                    placeholder="Como prefere ser chamado(a)"
                    value={formData.preferredName}
                    onChange={(e) => updateFormData({ preferredName: e.target.value })}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Nome social ou como prefere ser chamado(a). Opcional.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    CPF *
                  </label>
                  <Input
                    type="text"
                    placeholder="000.000.000-00"
                    value={formatCPFDisplay(formData.cpf)}
                    onChange={(e) => handleCPFChange(e.target.value)}
                    maxLength={14}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Seu CPF será usado para verificação de identidade
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Data de Nascimento *
                  </label>
                  <Input
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => updateFormData({ birthDate: e.target.value })}
                    className="w-full"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                {onSkip && (
                  <Button
                    variant="outline"
                    onClick={onSkip}
                    className="flex-1"
                  >
                    Pular por enquanto
                  </Button>
                )}
                <Button
                  onClick={goToNextStep}
                  disabled={!isPersonalStepValid}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  Continuar
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step: Contact Information */}
          {currentStep === "contact" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl mb-1 flex items-center gap-2">
                  <Phone className="w-6 h-6 text-primary" />
                  Informações de Contato
                </h2>
                <p className="text-muted-foreground text-sm">
                  Como podemos entrar em contato com você?
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    E-mail
                  </label>
                  <div className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg bg-secondary">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{email}</span>
                    <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Verificaremos seu e-mail em breve
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Telefone/Celular *
                  </label>
                  <Input
                    type="tel"
                    placeholder="(00) 00000-0000"
                    value={formatPhoneDisplay(formData.phone)}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    maxLength={15}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Será usado para verificação e contato com compradores
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={goToPreviousStep}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <Button
                  onClick={goToNextStep}
                  disabled={!isContactStepValid}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  Continuar
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step: Address */}
          {currentStep === "address" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl mb-1 flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-primary" />
                  Endereço Principal
                </h2>
                <p className="text-muted-foreground text-sm">
                  Onde você está localizado?
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    CEP *
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="00000-000"
                      value={formatCEPDisplay(formData.address.cep)}
                      onChange={(e) => handleCEPChange(e.target.value)}
                      maxLength={9}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCEPLookup}
                      disabled={formData.address.cep.length !== 8 || cepLookupLoading}
                    >
                      {cepLookupLoading ? "Buscando..." : "Buscar"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Digite o CEP para preencher automaticamente
                  </p>
                  {cepLookupError && (
                    <p className="text-sm text-destructive mt-1">{cepLookupError}</p>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Rua/Avenida *
                    </label>
                    <Input
                      type="text"
                      placeholder="Nome da rua"
                      value={formData.address.street}
                      onChange={(e) => updateAddress({ street: e.target.value })}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Número *
                    </label>
                    <Input
                      type="text"
                      placeholder="123"
                      value={formData.address.number}
                      onChange={(e) => updateAddress({ number: e.target.value })}
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Complemento
                  </label>
                  <Input
                    type="text"
                    placeholder="Apto, Bloco, Casa..."
                    value={formData.address.complement || ""}
                    onChange={(e) => updateAddress({ complement: e.target.value })}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Bairro *
                  </label>
                  <Input
                    type="text"
                    placeholder="Nome do bairro"
                    value={formData.address.neighborhood}
                    onChange={(e) => updateAddress({ neighborhood: e.target.value })}
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Cidade *
                    </label>
                    <Input
                      type="text"
                      placeholder="Cidade"
                      value={formData.address.city}
                      onChange={(e) => updateAddress({ city: e.target.value })}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Estado *
                    </label>
                    <select
                      value={formData.address.state}
                      onChange={(e) => updateAddress({ state: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {BRAZILIAN_STATES.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={goToPreviousStep}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <Button
                  onClick={goToNextStep}
                  disabled={!isAddressStepValid}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  Continuar
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step: Profile Photo */}
          {currentStep === "photo" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl mb-1 flex items-center gap-2">
                  <Camera className="w-6 h-6 text-primary" />
                  Foto de Perfil
                </h2>
                <p className="text-muted-foreground text-sm">
                  Adicione uma foto para personalizar seu perfil (opcional)
                </p>
              </div>

              <div className="flex flex-col items-center gap-6 py-8">
                {/* Avatar Preview */}
                <div className="relative">
                  <div className="w-32 h-32 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-4xl font-medium overflow-hidden">
                    {formData.profileImage ? (
                      <img
                        src={formData.profileImage}
                        alt="Profile preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      formData.fullName
                        .split(" ")
                        .map(n => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)
                    )}
                  </div>
                  <input
                    ref={profilePhotoInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/svg+xml"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    aria-label="Enviar foto de perfil"
                    onClick={() => profilePhotoInputRef.current?.click()}
                    className="absolute bottom-0 right-0 w-10 h-10 bg-primary hover:bg-primary/90 rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-colors"
                  >
                    <Camera className="w-5 h-5 text-primary-foreground" />
                  </button>
                </div>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Clique no ícone da câmera para fazer upload de uma foto
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Formatos aceitos: JPG, PNG, WebP, HEIC, SVG (máx. 5MB; SVG é convertido para JPEG)
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={goToPreviousStep}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <Button
                  onClick={goToNextStep}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  Continuar
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step: Review */}
          {currentStep === "review" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl mb-1 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-primary" />
                  Revisão dos Dados
                </h2>
                <p className="text-muted-foreground text-sm">
                  Confira suas informações antes de finalizar
                </p>
              </div>

              <div className="space-y-4">
                {/* Profile Preview */}
                <div className="bg-secondary rounded-lg p-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-xl font-medium overflow-hidden shrink-0">
                    {formData.profileImage ? (
                      <img
                        src={formData.profileImage}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      formData.fullName
                        .split(" ")
                        .map(n => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-lg">{formData.fullName}</h3>
                    <p className="text-sm text-muted-foreground">{email}</p>
                  </div>
                </div>

                {/* Personal Info */}
                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Informações Pessoais
                  </h3>
                  <div className="space-y-2 text-sm">
                    {formData.preferredName ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Nome de Preferência:</span>
                        <span className="font-medium">{formData.preferredName}</span>
                      </div>
                    ) : null}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">CPF:</span>
                      <span className="font-medium">{formatCPFDisplay(formData.cpf)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Data de Nascimento:</span>
                      <span className="font-medium">{formatDateDisplay(formData.birthDate)}</span>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    Contato
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Telefone:</span>
                      <span className="font-medium">{formatPhoneDisplay(formData.phone)}</span>
                    </div>
                  </div>
                </div>

                {/* Address Info */}
                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    Endereço
                  </h3>
                  <div className="text-sm space-y-1">
                    <p className="font-medium">
                      {formData.address.street}, {formData.address.number}
                      {formData.address.complement && ` - ${formData.address.complement}`}
                    </p>
                    <p className="text-muted-foreground">{formData.address.neighborhood}</p>
                    <p className="text-muted-foreground">
                      {formData.address.city} - {formData.address.state}
                    </p>
                    <p className="text-muted-foreground">
                      CEP: {formatCEPDisplay(formData.address.cep)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-900">
                  <strong>Importante:</strong> Suas informações serão usadas para verificação 
                  de identidade e melhorar a segurança da plataforma. Você poderá editá-las 
                  a qualquer momento em "Meu Perfil".
                </p>
              </div>

              {submitError && (
                <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                  {submitError}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={goToPreviousStep}
                  className="flex items-center gap-2"
                  disabled={isSubmitting}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <Button
                  onClick={handleComplete}
                  className="flex-1 flex items-center justify-center gap-2"
                  disabled={isSubmitting}
                >
                  <CheckCircle className="w-4 h-4" />
                  {isSubmitting ? "Salvando…" : "Finalizar Cadastro"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Skip Link (only on first step) */}
        {currentStep === "personal" && onSkip && (
          <div className="text-center mt-4">
            <button
              onClick={onSkip}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Prefiro preencher depois
            </button>
          </div>
        )}
      </div>
    </div>
  );
}